/**
 * 입력 검증 스키마
 * ---------------------------------------------------------------------------
 * 서버와 웹이 같은 스키마를 쓴다. 규칙이 갈라지면 브라우저에서는 통과하고
 * 서버에서 400 이 나는, 원인 찾기 나쁜 상황이 생긴다.
 *
 * DB 제약(CHECK)과 여기의 규칙은 **의도적으로 겹쳐 둔다.** 스키마는 사용자에게
 * 한글로 알려주기 위한 것이고, CHECK 는 어떤 경로로 들어와도 깨지지 않게
 * 하기 위한 것이다. 둘 중 하나만 두면 안 된다.
 */

import { z } from 'zod';
import { FACILITY_TYPES, LOG_STATUSES, LOG_TYPES, SITE_GRADES } from './types.js';

const uuid = z.string().uuid('올바른 식별자가 아닙니다');
/**
 * YYYY-MM-DD. 형식뿐 아니라 실제로 있는 날짜인지도 본다.
 *
 * 형식만 보면 2026-13-01 이나 2026-02-30 이 통과한다. 일지의 날짜 칸은
 * Postgres 의 date 라 결국 거절되지만, 날씨 조회처럼 DB 를 거치지 않고
 * 바깥으로 나가는 곳에서는 아무도 막지 않는다.
 */
export const isoDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, '날짜는 YYYY-MM-DD 형식이어야 합니다')
  .refine((value) => {
    const [y, m, d] = value.split('-').map(Number);
    const date = new Date(Date.UTC(y!, m! - 1, d!));
    return (
      date.getUTCFullYear() === y && date.getUTCMonth() === m! - 1 && date.getUTCDate() === d
    );
  }, '실제로 존재하는 날짜가 아닙니다');

const isoDate = isoDateSchema;
const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, '시각은 HH:MM 형식이어야 합니다');

/** 빈 문자열을 null 로 바꾼다. 폼에서 지운 값은 '' 로 오기 때문이다. */
const emptyToNull = <T extends z.ZodTypeAny>(schema: T) =>
  z.preprocess((v) => (v === '' ? null : v), schema.nullable().optional());

/** 측정값처럼 "비어 있을 수 있는 수치" 에 반복해서 쓴다. */
const optionalNumber = (min?: number, max?: number) => {
  let n = z.coerce.number();
  if (min !== undefined) n = n.min(min);
  if (max !== undefined) n = n.max(max);
  return emptyToNull(n);
};

const optionalText = (max: number) => emptyToNull(z.string().max(max));

/** 어느 행에나 붙는 표시 순서. */
const sortOrder = z.coerce.number().int().min(0).default(0);

// ----------------------------------------------------------------------------
// 사업장
// ----------------------------------------------------------------------------

export const siteInputSchema = z.object({
  siteCode: z.string().trim().min(1, '사업장 코드를 입력하세요').max(40),
  siteName: z.string().trim().min(1, '사업장명을 입력하세요').max(200),
  bizRegNo: emptyToNull(z.string().regex(/^\d{10}$/, '사업자등록번호는 숫자 10자리입니다')),
  permitNo: optionalText(60),
  siteGrade: emptyToNull(z.enum(SITE_GRADES)),
  bizType: optionalText(100),
  bizItem: optionalText(100),
  zipCode: optionalText(10),
  address1: optionalText(300),
  address2: optionalText(300),
  latitude: optionalNumber(-90, 90),
  longitude: optionalNumber(-180, 180),
  ceoName: optionalText(100),
  managerName: optionalText(100),
  managerPosition: optionalText(60),
  technicianName: optionalText(100),
  technicianGrade: optionalText(40),
  contactTel: optionalText(40),
  contactEmail: emptyToNull(z.string().email('이메일 형식이 아닙니다').max(200)),
  autoFillFacilities: z.boolean().optional(),
  isActive: z.boolean().optional(),
  openedOn: emptyToNull(isoDate),
  closedOn: emptyToNull(isoDate),
  note: optionalText(500),
});

export type SiteInputSchema = z.infer<typeof siteInputSchema>;

// ----------------------------------------------------------------------------
// 시설
// ----------------------------------------------------------------------------

export const facilityInputBaseSchema = z.object({
  siteId: uuid,
  facilityType: z.enum(FACILITY_TYPES),
  outletNo: optionalText(20),
  facilityCode: optionalText(40),
  facilityName: z.string().trim().min(1, '시설명을 입력하세요').max(200),
  installLocation: optionalText(200),
  capacity: optionalNumber(),
  capacityUnit: optionalText(20),
  powerCapacity: optionalNumber(0),
  pollutant: optionalText(200),
  treatmentMethod: optionalText(100),
  installedOn: emptyToNull(isoDate),
  removedOn: emptyToNull(isoDate),
  isActive: z.boolean().optional(),
  sortOrder: z.coerce.number().int().min(0).optional(),
  note: optionalText(500),
});

/** 신규 등록용. 필드 단위 규칙에 더해 필드 사이의 관계까지 본다. */
export const facilityInputSchema = facilityInputBaseSchema
  .refine((v) => !v.removedOn || !v.installedOn || v.removedOn >= v.installedOn, {
    message: '철거일이 설치일보다 빠릅니다',
    path: ['removedOn'],
  })
  // 대기 시설은 배출구 번호로 묶어 서식을 출력한다. 없으면 서식이 나오지 않는다.
  .refine(
    (v) => !(v.facilityType === 'AIR_OUTLET' || v.facilityType === 'AIR_PREVENTION') || !!v.outletNo,
    { message: '대기 시설은 배출구 번호가 필요합니다', path: ['outletNo'] },
  );

/** 부분 수정용. 보내지 않은 필드는 그대로 둔다. */
export const facilityPatchSchema = facilityInputBaseSchema.partial();

export type FacilityInputSchema = z.infer<typeof facilityInputSchema>;

// ----------------------------------------------------------------------------
// 일지 공통
// ----------------------------------------------------------------------------

export const maintenanceRowSchema = z
  .object({
    logMaintenanceId: uuid.optional(),
    facilityId: uuid.nullable().optional(),
    facilityName: z.string().trim().min(1, '시설명이 필요합니다').max(200),
    outletNo: optionalText(20),
    startedAt: emptyToNull(z.string().datetime({ offset: true })),
    finishedAt: emptyToNull(z.string().datetime({ offset: true })),
    maintainer: optionalText(100),
    details: optionalText(2000),
    sortOrder,
  })
  .refine((v) => !v.finishedAt || !v.startedAt || v.finishedAt >= v.startedAt, {
    message: '보수 종료가 시작보다 빠릅니다',
    path: ['finishedAt'],
  });

// ----------------------------------------------------------------------------
// T01
// ----------------------------------------------------------------------------

export const airOutletRowSchema = z
  .object({
    logAirOutletId: uuid.optional(),
    facilityId: uuid.nullable().optional(),
    outletNo: z.string().trim().min(1, '배출구 번호가 필요합니다').max(20),
    facilityName: z.string().trim().min(1, '시설명이 필요합니다').max(200),
    uptimeStart: emptyToNull(hhmm),
    uptimeEnd: emptyToNull(hhmm),
    operatingHours: optionalNumber(0, 24),
    materialName: optionalText(100),
    materialUsage: optionalNumber(0),
    materialUnit: optionalText(20),
    note: optionalText(500),
    sortOrder,
  })
  // 자정을 넘겨 가동하는 경우가 있어 start > end 를 막지 않는다.
  // 대신 한쪽만 채운 것은 실수로 본다.
  .refine((v) => !!v.uptimeStart === !!v.uptimeEnd, {
    message: '가동 시작과 종료 시각을 함께 입력하세요',
    path: ['uptimeEnd'],
  });

export const airPreventionRowSchema = z.object({
  logAirPreventionId: uuid.optional(),
  facilityId: uuid.nullable().optional(),
  outletNo: optionalText(20),
  facilityName: z.string().trim().min(1, '방지시설명이 필요합니다').max(200),
  installLocation: optionalText(200),
  powerConsumption: optionalNumber(0),
  treatmentCapacity: optionalNumber(0),
  powerCapacity: optionalNumber(0),
  pollutant: optionalText(100),
  concentration: optionalNumber(0),
  efficiency: optionalNumber(0, 100),
  drugName: optionalText(100),
  drugUse: optionalNumber(0),
  drugUnit: optionalText(20),
  note: optionalText(500),
  sortOrder,
});

// ----------------------------------------------------------------------------
// T02
// ----------------------------------------------------------------------------

export const materialRowSchema = z.object({
  id: uuid.optional(),
  materialName: z.string().trim().min(1, '원료명이 필요합니다').max(200),
  quantity: optionalNumber(0),
  unit: optionalText(20),
  note: optionalText(300),
  sortOrder,
});

export const waterConsignBodySchema = z
  .object({
    facilityId: uuid.nullable().optional(),
    facilityName: optionalText(200),
    uptimeStart: emptyToNull(hhmm),
    uptimeEnd: emptyToNull(hhmm),
    operatingHours: optionalNumber(0, 24),
    waterVolume: optionalNumber(0),
    wastewaterGeneration: optionalNumber(0),
    consignmentAmount: optionalNumber(0),
    storageWastewater: optionalNumber(0),
    confirmationSerialNo: optionalText(60),
    processingCompany: optionalText(200),
    processingCompanyRegNo: optionalText(20),
    note: optionalText(1000),
  })
  .refine((v) => !!v.uptimeStart === !!v.uptimeEnd, {
    message: '가동 시작과 종료 시각을 함께 입력하세요',
    path: ['uptimeEnd'],
  });

// ----------------------------------------------------------------------------
// T03
// ----------------------------------------------------------------------------

/** 24시간 가동표. 길이가 어긋나면 서식이 깨지므로 여기서 막는다. */
const hourFlags = z
  .array(z.boolean())
  .length(24, '가동표는 24칸이어야 합니다')
  .default(() => Array.from({ length: 24 }, () => false));

export const facilityHoursRowSchema = z.object({
  id: uuid.optional(),
  facilityId: uuid.nullable().optional(),
  facilityName: z.string().trim().min(1, '시설명이 필요합니다').max(200),
  hours: hourFlags,
  note: optionalText(300),
  sortOrder,
});

export const preventionHoursRowSchema = facilityHoursRowSchema.extend({
  workerName: optionalText(100),
});

export const waterUsageRowSchema = z.object({
  id: uuid.optional(),
  category: z.string().trim().min(1, '구분을 입력하세요').max(60),
  prevGauge: optionalNumber(0),
  currGauge: optionalNumber(0),
  usageAmount: optionalNumber(0),
  checkTime: emptyToNull(hhmm),
  note: optionalText(300),
  sortOrder,
});

export const sludgeRowSchema = z.object({
  id: uuid.optional(),
  generationAmount: optionalNumber(0),
  treatmentAmount: optionalNumber(0),
  storageAmount: optionalNumber(0),
  waterContent: optionalNumber(0, 100),
  storageLocation: optionalText(200),
  selfTreatmentLocation: optionalText(200),
  outsourcedCompany: optionalText(200),
  note: optionalText(300),
  sortOrder,
});

export const chemicalRowSchema = z.object({
  id: uuid.optional(),
  chemicalName: z.string().trim().min(1, '약품명이 필요합니다').max(200),
  purchaseAmount: optionalNumber(0),
  usageAmount: optionalNumber(0),
  remainingAmount: optionalNumber(0),
  unit: optionalText(20),
  remarks: optionalText(300),
  sortOrder,
});

export const analysisRowSchema = z.object({
  id: uuid.optional(),
  category: z.string().trim().min(1, '측정 지점을 입력하세요').max(60),
  phValue: optionalNumber(0, 14),
  bodValue: optionalNumber(0),
  tocValue: optionalNumber(0),
  codValue: optionalNumber(0),
  ssValue: optionalNumber(0),
  nHexValue: optionalNumber(0),
  cnValue: optionalNumber(0),
  cuValue: optionalNumber(0),
  userDef1: optionalNumber(),
  userDef2: optionalNumber(),
  userDef3: optionalNumber(),
  userDef4: optionalNumber(),
  valueNote: optionalText(300),
  analyzedAt: emptyToNull(z.string().datetime({ offset: true })),
  sortOrder,
});

export const autoCheckRowSchema = z.object({
  id: uuid.optional(),
  checkItem: z.string().trim().min(1, '점검 항목을 입력하세요').max(100),
  phStatus: optionalText(40),
  bodStatus: optionalText(40),
  tocStatus: optionalText(40),
  codStatus: optionalText(40),
  ssStatus: optionalText(40),
  tnStatus: optionalText(40),
  tpStatus: optionalText(40),
  flowStatus: optionalText(40),
  samplerStatus: optionalText(40),
  note: optionalText(300),
  sortOrder,
});

export const waterSelfBodySchema = z.object({
  // 여러 명을 줄바꿈으로 적는 칸이라 한 사람 이름 길이로 재면 안 된다.
  shiftWorkers: optionalText(1000),

  powerOperatingTime: optionalText(30),
  powerUsageAmount: optionalNumber(0),
  powerPerWater: optionalNumber(0),
  powerCheckTime: emptyToNull(hhmm),
  powerTotalGauge: optionalNumber(0),
  powerRemarks: optionalText(500),

  aerationPh: optionalNumber(0, 14),
  aerationWaterTemp: optionalNumber(-60, 120),
  aerationDo: optionalNumber(0),
  aerationSv30: optionalNumber(0),
  aerationMlss: optionalNumber(0),
  aerationSvi: optionalNumber(0),
  aerationTime: optionalText(30),
  aerationMicrobeStatus: optionalText(300),

  breakdownStatus: optionalText(300),
  specialNotes: optionalText(4000),
  inspectionDetails: optionalText(4000),

  analyzerName: optionalText(100),
  outsourcedAnalysisCompany: optionalText(200),

  userDefTitle1: optionalText(60),
  userDefTitle2: optionalText(60),
  userDefTitle3: optionalText(60),
  userDefTitle4: optionalText(60),
});

// ----------------------------------------------------------------------------
// 일지 전체
// ----------------------------------------------------------------------------

/** 한 일지의 본문 행 수 상한. 실무에서 이만큼 나올 일이 없다. */
const MAX_ROWS = 200;

export const dailyLogInputSchema = z
  .object({
    siteId: uuid,
    logType: z.enum(LOG_TYPES),
    logDate: isoDate,
    weather: optionalText(30),
    // WMO 코드. 화면이 아이콘을 고르는 근거이며 사용자가 직접 넣지는 않는다.
    weatherCode: emptyToNull(z.coerce.number().int().min(0).max(99)),
    temperature: optionalNumber(-60, 60),
    note: optionalText(4000),

    /**
     * 확인자. 배열 순서가 결재란 순서다.
     *
     * 20명 상한은 DB 제약(seq 0~20)과 같다. 결재란이 그보다 길어질 일은
     * 없고, 실수로 전 직원을 넣는 것을 막는다.
     */
    approverUserIds: z.array(uuid).max(20, '확인자는 20명까지 지정할 수 있습니다').optional(),

    maintenances: z.array(maintenanceRowSchema).max(MAX_ROWS).optional(),

    airOutlets: z.array(airOutletRowSchema).max(MAX_ROWS).optional(),
    airPreventions: z.array(airPreventionRowSchema).max(MAX_ROWS).optional(),

    waterConsign: waterConsignBodySchema.nullable().optional(),
    consignMaterials: z.array(materialRowSchema).max(MAX_ROWS).optional(),

    waterSelf: waterSelfBodySchema.nullable().optional(),
    facilityHours: z.array(facilityHoursRowSchema).max(MAX_ROWS).optional(),
    preventionHours: z.array(preventionHoursRowSchema).max(MAX_ROWS).optional(),
    waterUsages: z.array(waterUsageRowSchema).max(MAX_ROWS).optional(),
    sludges: z.array(sludgeRowSchema).max(MAX_ROWS).optional(),
    selfMaterials: z.array(materialRowSchema).max(MAX_ROWS).optional(),
    chemicals: z.array(chemicalRowSchema).max(MAX_ROWS).optional(),
    analyses: z.array(analysisRowSchema).max(MAX_ROWS).optional(),
    autoChecks: z.array(autoCheckRowSchema).max(MAX_ROWS).optional(),
  })
  // 유형에 맞지 않는 본문이 섞여 오면 막는다. 서버가 조용히 버리면 화면에서는
  // 저장된 줄 알고 다음 날 사라진 것을 발견하게 된다.
  .superRefine((v, ctx) => {
    const wrong = (field: string, allowed: string) =>
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [field],
        message: `${allowed} 일지에만 담을 수 있는 항목입니다`,
      });

    if (v.logType !== 'T01_AIR') {
      if (v.airOutlets?.length) wrong('airOutlets', '대기');
      if (v.airPreventions?.length) wrong('airPreventions', '대기');
    }
    if (v.logType !== 'T02_WATER_CONSIGN') {
      if (v.waterConsign) wrong('waterConsign', '폐수 위탁');
      if (v.consignMaterials?.length) wrong('consignMaterials', '폐수 위탁');
    }
    if (v.logType !== 'T03_WATER_SELF') {
      if (v.waterSelf) wrong('waterSelf', '폐수 자가운영');
      if (v.facilityHours?.length) wrong('facilityHours', '폐수 자가운영');
      if (v.preventionHours?.length) wrong('preventionHours', '폐수 자가운영');
      if (v.waterUsages?.length) wrong('waterUsages', '폐수 자가운영');
      if (v.sludges?.length) wrong('sludges', '폐수 자가운영');
      if (v.selfMaterials?.length) wrong('selfMaterials', '폐수 자가운영');
      if (v.chemicals?.length) wrong('chemicals', '폐수 자가운영');
      if (v.analyses?.length) wrong('analyses', '폐수 자가운영');
      if (v.autoChecks?.length) wrong('autoChecks', '폐수 자가운영');
    }
  });

export type DailyLogInputSchema = z.infer<typeof dailyLogInputSchema>;

/** 목록 조회 조건. */
export const dailyLogQuerySchema = z.object({
  siteId: uuid.optional(),
  logType: z.enum(LOG_TYPES).optional(),
  from: isoDate.optional(),
  to: isoDate.optional(),
  status: z.enum(LOG_STATUSES).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
});

/**
 * 일지를 다른 날짜로 복사한다. 현장에서 가장 많이 쓰는 기능이다.
 *
 * 여러 날짜를 한 번에 받는다. 며칠 또는 한 달을 밀린 채로 발견하는 일이
 * 흔한데, 하루씩 복사하게 하면 서른 번을 눌러야 한다.
 *
 * 상한을 62일로 둔 이유: 두 달이면 어떤 밀린 상황도 덮는다. 그보다 많으면
 * 복사가 아니라 다른 문제다.
 */
export const dailyLogCopySchema = z.object({
  sourceDailyLogId: uuid,
  targetDates: z.array(isoDate).min(1, '복사할 날짜를 고르세요').max(62),
  /**
   * 복사 대상 날짜의 날씨를 사업장 좌표로 새로 받아 채울지.
   * 받지 못하면 원본 값을 그대로 둔다.
   */
  applyWeather: z.boolean().default(true),
});

// ----------------------------------------------------------------------------
// 자가측정 서류
// ----------------------------------------------------------------------------

export const selfMeasurementInputSchema = z.object({
  siteId: uuid,
  title: z.string().trim().min(1, '제목을 입력하세요').max(300),
  measuredYear: z.coerce.number().int().min(2000).max(2200),
  half: emptyToNull(z.coerce.number().int().refine((v) => v === 1 || v === 2, '1 또는 2')),
  agencyName: optionalText(200),
  measuredOn: emptyToNull(isoDate),
  note: optionalText(1000),
});

export const selfMeasurementQuerySchema = z.object({
  siteId: uuid.optional(),
  year: z.coerce.number().int().min(2000).max(2200).optional(),
});

// ----------------------------------------------------------------------------
// 인쇄
// ----------------------------------------------------------------------------

/**
 * 법정 서식 인쇄.
 *
 * 목록에서 여러 건을 골라 한 번에 뽑는다. 한 달치를 하루씩 인쇄하면 서른
 * 번을 눌러야 하고, 그 사이에 한 장을 빠뜨린다.
 *
 * 상한을 100건으로 둔 이유: 한 번에 만드는 HTML 이 지나치게 커지면 브라우저
 * 인쇄 미리보기가 멈춘다. 석 달치면 충분하다.
 */
export const dailyLogPrintSchema = z.object({
  dailyLogIds: z.array(uuid).min(1, '인쇄할 일지를 고르세요').max(100),
  /**
   * 서식의 "3. 자가측정사항" 자리에 적을 성적서.
   * 비우면 사업장별 최신 성적서를 자동으로 고른다.
   */
  selfMeasurementId: emptyToNull(uuid),
});

// ----------------------------------------------------------------------------
// 결재 확인자
// ----------------------------------------------------------------------------

/** 사업장 기본 확인자. 배열 순서가 결재란 순서다. */
export const siteApproverInputSchema = z.object({
  userIds: z.array(uuid).max(20, '확인자는 20명까지 지정할 수 있습니다'),
});

/**
 * 여러 일지를 한 번에 확인한다. 목록의 다중 선택이 쓴다.
 *
 * 상한을 200건으로 둔 이유: 한 건마다 트랜잭션을 열기 때문에 그보다 많으면
 * 요청이 길어져 화면이 멈춘 것처럼 보인다. 석 달치보다 많다.
 */
export const bulkConfirmSchema = z.object({
  dailyLogIds: z.array(uuid).min(1, '확인할 일지를 고르세요').max(200),
});

// ----------------------------------------------------------------------------
// 대시보드
// ----------------------------------------------------------------------------

/**
 * 기업별 대시보드 설정.
 *
 * 한 개 이상은 켜 두어야 한다. 전부 끄면 대시보드가 빈 화면이 되고, 사용자는
 * 고장 난 줄 안다.
 */
export const dashboardSettingSchema = z.object({
  enabledLogTypes: z
    .array(z.enum(LOG_TYPES))
    .min(1, '일지를 한 가지 이상 켜 두세요')
    .transform((v) => [...new Set(v)]),
  countNonWorkingDays: z.boolean(),
});

export const calendarQuerySchema = z.object({
  siteId: uuid.optional(),
  year: z.coerce.number().int().min(2000).max(2100),
  month: z.coerce.number().int().min(1).max(12),
});

/**
 * 차트 기간.
 *
 * 상한을 366일로 둔 이유: 한 해를 넘기면 점이 너무 많아 선이 뭉개진다.
 * 그보다 긴 추이는 월간 집계로 봐야 한다.
 */
export const chartQuerySchema = z
  .object({
    siteId: uuid.optional(),
    from: isoDate,
    to: isoDate,
  })
  .refine((v) => v.from <= v.to, { message: '시작일이 종료일보다 늦습니다', path: ['to'] })
  .refine(
    (v) => (Date.parse(v.to) - Date.parse(v.from)) / 86_400_000 <= 366,
    { message: '차트 기간은 1년 이내로 고르세요', path: ['to'] },
  );
